
import React from "react";
import { BriefcaseBusiness } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

interface TeamTemplatesProps {
  setActiveTab: (tab: string) => void;
}

export const TeamTemplates: React.FC<TeamTemplatesProps> = ({ setActiveTab }) => {
  const { toast } = useToast();
  
  const templates = [
    {
      name: "Web Development Team",
      description: "Full-stack team with frontend, backend, and DevOps expertise.",
      roles: ["Frontend Developer", "Backend Developer", "DevOps Engineer", "UX Designer"],
      tags: ["Web", "Full-Stack"]
    },
    {
      name: "Mobile App Team",
      description: "Cross-platform mobile development and design team.",
      roles: ["Mobile Developer", "UI Designer", "Backend Developer", "QA Tester"],
      tags: ["Mobile", "Apps"]
    },
    {
      name: "Data Science Team",
      description: "Team specialized in data analysis and machine learning.",
      roles: ["Data Scientist", "ML Engineer", "Data Engineer", "Data Analyst"],
      tags: ["Data", "AI/ML"]
    }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BriefcaseBusiness className="h-5 w-5 text-indigo-500" />
          Team Templates
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((template, index) => (
            <Card key={index} className="border border-gray-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{template.name}</CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                <div className="mb-3">
                  <div className="text-sm font-medium mb-1">Roles:</div>
                  <div className="flex flex-wrap gap-1">
                    {template.roles.map((role, i) => (
                      <Badge key={i} variant="outline" className="bg-gray-100">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  {template.tags.map((tag, i) => (
                    <Badge key={i} className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  variant="outline"
                  onClick={() => {
                    setActiveTab("build");
                    toast({
                      title: "Template Selected",
                      description: `${template.name} template has been loaded.`,
                      variant: "success"
                    });
                  }}
                >
                  Use Template
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
